import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import { 
  Search, CheckCircle, Clock, AlertCircle, XCircle, 
  FileText, ChevronRight, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: FileText },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending_documents: { label: 'Pending Documents', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Not Approved', color: 'bg-red-100 text-red-700', icon: XCircle },
  withdrawn: { label: 'Withdrawn', color: 'bg-gray-100 text-gray-700', icon: XCircle }
};

const CoachApplicationStatusPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const res = await axios.get(`${API_URL}/api/coach-applications/check-status/${encodeURIComponent(email)}`);
      setResult(res.data);
    } catch (err) {
      console.error('Failed to check status:', err);
      setError('Failed to check status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const StatusDisplay = () => {
    if (!result) return null;
    
    if (!result.found) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Application Found</h3>
          <p className="text-gray-500 mb-6">
            We couldn't find an application with that email address.
          </p>
          <Button 
            onClick={() => navigate('/coach-application')}
            className="bg-navy hover:bg-navy/90"
          >
            Submit an Application
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      );
    }
    
    const statusConfig = STATUS_CONFIG[result.status] || STATUS_CONFIG.submitted;
    const StatusIcon = statusConfig.icon;
    
    return (
      <div className="text-center py-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${statusConfig.color}`}>
          <StatusIcon className="w-10 h-10" />
        </div>
        
        <h3 className="text-xl font-bold text-navy mb-2">
          Hello, {result.first_name}!
        </h3>
        
        <Badge className={`${statusConfig.color} text-sm px-4 py-1 mb-4`}>
          {statusConfig.label}
        </Badge>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Application ID</p>
              <p className="font-mono text-navy">{result.application_id}</p>
            </div>
            <div>
              <p className="text-gray-500">Submitted</p>
              <p className="text-gray-700">
                {new Date(result.submitted_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        
        {/* Status-specific messages */}
        {result.status === 'submitted' && (
          <p className="text-gray-600 text-sm">
            Your application has been received and is awaiting review.
            We'll contact you via email with updates.
          </p>
        )}
        
        {result.status === 'under_review' && (
          <p className="text-gray-600 text-sm">
            Your application is currently being reviewed by our team.
            We'll be in touch soon!
          </p>
        )}
        
        {result.status === 'pending_documents' && (
          <div>
            <p className="text-orange-600 text-sm mb-4">
              We need additional documents to complete your application.
              Please check your email for instructions.
            </p>
            <Button 
              onClick={() => navigate(`/coach-application/${result.application_id}/documents`)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Upload Documents
            </Button>
          </div>
        )}
        
        {result.status === 'approved' && (
          <div>
            <p className="text-green-600 text-sm mb-4">
              Congratulations! Your application has been approved.
              Check your email for login instructions and next steps.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-green-600 hover:bg-green-700"
            >
              Sign In to Your Account
            </Button>
          </div>
        )}
        
        {result.status === 'rejected' && (
          <p className="text-gray-600 text-sm">
            Unfortunately, your application was not approved at this time.
            Please check your email for more information.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <div className="mb-4">
          <BackButton to="/" label="Back to Home" />
        </div>
        
        {/* Header - Athletic Style */}
        <div className="text-center mb-8">
          <img 
            src="/chiefs-logo.png" 
            alt="Park City Chiefs" 
            className="h-20 mx-auto mb-4"
            onError={(e) => e.target.style.display = 'none'}
          />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Coach Portal</p>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter mb-2">Application Status</h1>
          <p className="text-gray-600 font-body">Check the status of your coach application</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {/* Search Form */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="flex space-x-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Enter your email"
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleSearch}
                    disabled={loading}
                    className="bg-navy hover:bg-navy/90"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
              
              {error && (
                <p className="text-red text-sm">{error}</p>
              )}
            </div>
            
            {/* Results */}
            <StatusDisplay />
          </CardContent>
        </Card>

        {/* Back Link */}
        <div className="text-center mt-6">
          <button 
            onClick={() => navigate('/coach-application')}
            className="text-navy text-sm hover:underline flex items-center justify-center mx-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Application Form
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachApplicationStatusPage;
